import React, { useState, useRef, useEffect } from 'react';
import { HistoryPesee } from '@/services/historyService';
import historyService, { isPeseeDeletable, requiresAdminToEditPesee } from '@/services/historyService';
import ticketActionsService from '@/services/ticketActionsService';
import refoulementTicketService from '@/services/refoulementTicketService';
import { convertToRefoulementTicketData } from '@/utils/refoulementTicketConverter';
import weighingService from '@/services/weighing';
import PeseeModificationModal from './modals/PeseeModificationModal';
import { RefoulementModal } from '@/components/modals';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS, PermissionCode, PLATFORM_ROLES, OPERATIONAL_ROLES } from '@/types/permissions';
import type { Pesée } from '@/types/weighing';

/** Rôles autorisés à modifier une pesée en cours */
const MODIFY_PESEE_ROLES = [
  PLATFORM_ROLES.PLATFORM_SUPER_ADMIN,
  PLATFORM_ROLES.PLATFORM_SUPPORT,
  PLATFORM_ROLES.PLATFORM_ADMIN,
  OPERATIONAL_ROLES.SITE_MANAGER,
  OPERATIONAL_ROLES.SITE_SUPERVISOR,
  OPERATIONAL_ROLES.TENANT_ADMIN,
  OPERATIONAL_ROLES.TENANT_MANAGER,
  OPERATIONAL_ROLES.TENANT_OWNER,
  OPERATIONAL_ROLES.SITE_ADMIN,
] as const;

/** Rôles admin uniquement — modification des pesées COMPLETEE / ACHEVEE */
const ADMIN_MODIFY_COMPLETED_ROLES = [
  PLATFORM_ROLES.PLATFORM_SUPER_ADMIN,
  PLATFORM_ROLES.PLATFORM_ADMIN,
  OPERATIONAL_ROLES.TENANT_OWNER,
  OPERATIONAL_ROLES.TENANT_ADMIN,
  OPERATIONAL_ROLES.SITE_ADMIN,
] as const;

interface HistoryActionsMenuProps {
  pesee: HistoryPesee;
  onAction: (action: string, pesee: HistoryPesee) => void | Promise<void>;
  onClose: () => void;
  position: { x: number; y: number };
}

const HistoryActionsMenu: React.FC<HistoryActionsMenuProps> = ({
  pesee,
  onAction,
  onClose,
  position
}) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectMotif, setRejectMotif] = useState('');
  const [showActorsModal, setShowActorsModal] = useState(false);
  const [showModificationModal, setShowModificationModal] = useState(false);
  const [showRefoulementModal, setShowRefoulementModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { hasPermission, hasAnyRole } = usePermissions();

  const canModifyInProgressPesee = hasAnyRole([...MODIFY_PESEE_ROLES]);
  const canModifyCompletedPesee = hasAnyRole([...ADMIN_MODIFY_COMPLETED_ROLES]);
  const canModifyPesee = requiresAdminToEditPesee(pesee.statut)
    ? canModifyCompletedPesee
    : canModifyInProgressPesee;

  // Fermer le menu si on clique à l'extérieur (sans fermer les modals ouverts)
  useEffect(() => {
    if (showDeleteConfirm || isDeleting) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        showRefoulementModal ||
        showModificationModal ||
        showRejectModal ||
        showActorsModal
      ) {
        return;
      }

      const target = event.target as HTMLElement;
      if (target.closest('[data-history-modal="true"]')) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // click plutôt que mousedown : évite de fermer le menu avant le handler du bouton
    const timeoutId = window.setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [
    onClose,
    showRefoulementModal,
    showModificationModal,
    showRejectModal,
    showActorsModal,
    showDeleteConfirm,
    isDeleting,
  ]);

  const getActionPermission = (action: string): PermissionCode => {
    const permissions: { [key: string]: PermissionCode } = {
      voir_detail: PERMISSIONS.WEIGHING_VIEW,
      continuer: PERMISSIONS.WEIGHING_EDIT,
      refuser: PERMISSIONS.WEIGHING_EDIT,
      refouler: PERMISSIONS.WEIGHING_EDIT,
      supprimer: PERMISSIONS.WEIGHING_DELETE,
      voir_acteurs: PERMISSIONS.WEIGHING_VIEW,
      imprimer_ticket: PERMISSIONS.AUDIT_REPORTS,
      exporter_pdf: PERMISSIONS.AUDIT_REPORTS,
      envoyer_email: PERMISSIONS.AUDIT_REPORTS,
      ajouter_stock: PERMISSIONS.STOCK_CREATE,
      modifier: PERMISSIONS.WEIGHING_EDIT
    };
    return permissions[action] || PERMISSIONS.WEIGHING_VIEW;
  };

  const availableActions = historyService.getAvailableActions(
    pesee.statut,
    pesee.en_stock,
    {
      poids_brut: pesee.poids_brut,
      poids_tare: pesee.poids_tare,
    },
  );

  // Filtrer les actions disponibles selon les permissions
  let filteredActions = availableActions.filter(action => {
    if (action === 'modifier' && !canModifyPesee) {
      return false;
    }
    if (action === 'supprimer') {
      if (hasPermission(PERMISSIONS.WEIGHING_DELETE)) {
        return true;
      }
      // Pesées non finalisées : le droit de modification suffit
      return hasPermission(PERMISSIONS.WEIGHING_EDIT) && isPeseeDeletable(pesee.statut);
    }
    const permission = getActionPermission(action);
    return hasPermission(permission);
  });

  // Ajouter l'action "refouler" si la pesée n'est pas COMPLETEE
  if (pesee.statut !== 'COMPLETEE' && !filteredActions.includes('refouler')) {
    const refoulerPermission = getActionPermission('refouler');
    if (hasPermission(refoulerPermission)) {
      filteredActions.push('refouler');
    }
  }

  const getActionIcon = (action: string) => {
    const icons: { [key: string]: string } = {
      voir_detail: 'fas fa-eye',
      continuer: 'fas fa-play',
      refuser: 'fas fa-times',
      refouler: 'fas fa-ban',
      supprimer: 'fas fa-trash',
      voir_acteurs: 'fas fa-users',
      imprimer_ticket: 'fas fa-print',
      exporter_pdf: 'fas fa-file-pdf',
      envoyer_email: 'fas fa-envelope',
      ajouter_stock: 'fas fa-plus-circle',
      modifier: 'fas fa-edit'
    };
    return icons[action] || 'fas fa-cog';
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      voir_detail: 'Voir le détail',
      continuer: 'Continuer la pesée',
      refuser: 'Réfuser la pesée',
      refouler: 'Refouler la pesée',
      supprimer: 'Supprimer définitivement',
      voir_acteurs: 'Voir les acteurs',
      imprimer_ticket: 'Imprimer le ticket',
      exporter_pdf: 'Exporter le ticket en PDF',
      envoyer_email: 'Envoyer le ticket par mail',
      ajouter_stock: 'Ajouter au stock',
      modifier: 'Modifier la pesée'
    };
    return labels[action] || action;
  };

  const getActionColor = (action: string) => {
    const colors: { [key: string]: string } = {
      voir_detail: 'text-blue-600 hover:bg-blue-50',
      continuer: 'text-green-600 hover:bg-green-50',
      refuser: 'text-red-600 hover:bg-red-50',
      refouler: 'text-red-600 hover:bg-red-50',
      supprimer: 'text-red-600 hover:bg-red-50',
      voir_acteurs: 'text-purple-600 hover:bg-purple-50',
      imprimer_ticket: 'text-orange-600 hover:bg-orange-50',
      exporter_pdf: 'text-blue-600 hover:bg-blue-50',
      envoyer_email: 'text-indigo-600 hover:bg-indigo-50',
      ajouter_stock: 'text-green-600 hover:bg-green-50',
      modifier: 'text-indigo-600 hover:bg-indigo-50'
    };
    return colors[action] || 'text-gray-600 hover:bg-gray-50';
  };

  const handleActionClick = async (action: string) => {
    switch (action) {
      case 'voir_detail':
        onAction('voir_detail', pesee);
        // Ne pas fermer le menu immédiatement pour voir_detail
        // Le menu sera fermé par HistoryPage.tsx après ouverture du modal
        break;
      case 'continuer':
        onAction('continuer', pesee);
        onClose();
        break;
      case 'refuser':
        setShowRejectModal(true);
        // Ne pas fermer le menu, on affiche le modal de refus
        break;
      case 'refouler':
        setShowRefoulementModal(true);
        // Ne pas fermer le menu, on affiche le modal de refoulement
        break;
      case 'supprimer':
        setShowDeleteConfirm(true);
        break;
      case 'voir_acteurs':
        setShowActorsModal(true);
        // Ne pas fermer le menu, on affiche le modal des acteurs
        break;
      case 'imprimer_ticket':
        try {
          const isRefoulee = pesee.statut === 'REFOULEE';
          
          if (isRefoulee) {
            // Récupérer les données de refoulement
            const peseeId = String(pesee.id);
            const refoulementData = await historyService.getRefoulementData(peseeId);
            
            if (refoulementData) {
              // Récupérer les données complètes de la pesée
              let peseeComplete: HistoryPesee | Pesée = pesee;
              try {
                const peseeIdNum = typeof pesee.id === 'string' ? parseInt(pesee.id, 10) : Number(pesee.id);
                const peseeResponse = await weighingService.getPesée(peseeIdNum);
                if (peseeResponse?.data) {
                  peseeComplete = peseeResponse.data as Pesée;
                }
              } catch (error) {
                console.warn('Impossible de récupérer les données complètes de la pesée, utilisation des données disponibles');
              }
              
              // Convertir en RefoulementTicketData
              const refoulementTicketData = convertToRefoulementTicketData(refoulementData, peseeComplete);
              
              // Imprimer le ticket de refoulement
              await refoulementTicketService.printTicket(refoulementTicketData, {
                onError: (error) => {
                  console.error('Erreur lors de l\'impression du ticket de refoulement:', error);
                  alert('Erreur lors de l\'impression du ticket de refoulement');
                }
              });
            } else {
              alert('Aucune donnée de refoulement trouvée pour cette pesée');
            }
          } else {
            // Ticket normal pour les autres statuts
            const ticketData = historyService.convertPeseeToWeighingTicketData(pesee);
            await ticketActionsService.printTicket(ticketData);
          }
        } catch (error: any) {
          console.error('Erreur lors de l\'impression:', error);
          alert(error.message || 'Erreur lors de l\'impression du ticket');
        }
        onClose();
        break;
      case 'exporter_pdf':
        try {
          if (pesee.statut === 'REFOULEE') {
            // Pour les pesées refoulées, récupérer les données de refoulement
            const refoulementData = await historyService.getRefoulementData(pesee.id);
            if (refoulementData) {
              const ticketData = historyService.convertPeseeToWeighingTicketData(pesee);
              // Ajouter les données de refoulement au ticket
              ticketData.refoulement = {
                numero_refoulement: refoulementData.numero_refoulement,
                date_refoulement: refoulementData.date_refoulement,
                motifs: refoulementData.motifs || [],
                observations: refoulementData.observations || '',
                agent_refoulement: refoulementData.agent_refoulement_nom || refoulementData.agent_refoulement_username || 'Agent'
              };
              await ticketActionsService.exportTicketPDF(ticketData);
            } else {
             
              // Fallback vers le ticket normal
              const ticketData = historyService.convertPeseeToWeighingTicketData(pesee);
              await ticketActionsService.exportTicketPDF(ticketData);
            }
          } else {
            // Ticket normal pour les autres statuts
            const ticketData = historyService.convertPeseeToWeighingTicketData(pesee);
            await ticketActionsService.exportTicketPDF(ticketData);
          }
        } catch (error) {
          console.error('Erreur lors de l\'export PDF:', error);
        }
        onClose();
        break;
      case 'envoyer_email':
        onAction('envoyer_email', pesee);
        // Ne pas fermer le menu immédiatement pour envoyer_email
        // Le menu sera fermé par HistoryPage.tsx après ouverture du modal
        break;
      case 'ajouter_stock':
        if (window.confirm('Êtes-vous sûr de vouloir ajouter cette pesée au stock ?')) {
          onAction('ajouter_stock', pesee);
        }
        onClose();
        break;
      case 'modifier':
        setShowModificationModal(true);
        // Ne pas fermer le menu, on affiche le modal de modification
        break;
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onAction('supprimer', pesee);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setIsDeleting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectMotif.trim()) {
      alert('Veuillez saisir un motif de refus');
      return;
    }
    
    try {
      await historyService.rejectPesee(pesee.id, rejectMotif);
      onAction('refuser', pesee);
      setShowRejectModal(false);
      setRejectMotif('');
      onClose(); // Fermer le menu après le refus
    } catch (error) {
      console.error('Erreur lors du refus:', error);
      alert('Erreur lors du refus de la pesée');
    }
  };

  return (
    <>
      {!showModificationModal && !showRejectModal && !showActorsModal && !showDeleteConfirm && (
      <div
        ref={menuRef}
        className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 min-w-48"
        style={{
          left: position.x,
          top: position.y,
          maxHeight: '400px',
          overflowY: 'auto'
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {filteredActions.map((action) => (
          <button
            key={action}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void handleActionClick(action);
            }}
            className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-3 ${getActionColor(action)}`}
          >
            <i className={`${getActionIcon(action)} w-4`}></i>
            <span>{getActionLabel(action)}</span>
          </button>
        ))}
      </div>
      )}

      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          data-history-modal="true"
        >
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-semibold mb-2 text-gray-900">Supprimer la pesée</h3>
            <p className="text-sm text-gray-600 mb-4">
              Êtes-vous sûr de vouloir supprimer définitivement la pesée{' '}
              <span className="font-medium text-gray-900">
                {pesee.reference || pesee.référence || `#${pesee.id}`}
              </span>{' '}
              ? Cette action est irréversible.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Suppression…' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de refus */}
      {showRejectModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          data-history-modal="true"
        >
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Refuser la pesée</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motif du refus
              </label>
              <textarea
                value={rejectMotif}
                onChange={(e) => setRejectMotif(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows={4}
                placeholder="Saisissez le motif du refus..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  onClose(); // Fermer le menu quand on annule
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Refuser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal des acteurs */}
      {showActorsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          data-history-modal="true"
        >
          <div className="bg-white rounded-lg p-6 w-96 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Acteurs de la pesée</h3>
              <button
                onClick={() => {
                  setShowActorsModal(false);
                  onClose(); // Fermer le menu quand on ferme le modal des acteurs
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="space-y-3">
              {pesee.client_nom && (
                <div className="flex items-center space-x-3 p-2 bg-blue-50 rounded">
                  <i className="fas fa-building text-blue-600"></i>
                  <div>
                    <div className="font-medium">Client</div>
                    <div className="text-sm text-gray-600">{pesee.client_nom}</div>
                    {pesee.client_info?.email && (
                      <div className="text-xs text-gray-500">{pesee.client_info.email}</div>
                    )}
                  </div>
                </div>
              )}
              
              {pesee.fournisseur_nom && (
                <div className="flex items-center space-x-3 p-2 bg-green-50 rounded">
                  <i className="fas fa-truck text-green-600"></i>
                  <div>
                    <div className="font-medium">Fournisseur</div>
                    <div className="text-sm text-gray-600">{pesee.fournisseur_nom}</div>
                    {pesee.fournisseur_info?.email && (
                      <div className="text-xs text-gray-500">{pesee.fournisseur_info.email}</div>
                    )}
                  </div>
                </div>
              )}
              
              {pesee.chauffeur_nom && (
                <div className="flex items-center space-x-3 p-2 bg-orange-50 rounded">
                  <i className="fas fa-user-tie text-orange-600"></i>
                  <div>
                    <div className="font-medium">Chauffeur</div>
                    <div className="text-sm text-gray-600">{pesee.chauffeur_nom}</div>
                    {pesee.chauffeur_info?.email && (
                      <div className="text-xs text-gray-500">{pesee.chauffeur_info.email}</div>
                    )}
                    {pesee.chauffeur_info?.telephone && (
                      <div className="text-xs text-gray-500">{pesee.chauffeur_info.telephone}</div>
                    )}
                  </div>
                </div>
              )}
              
              {pesee.exportateur_nom && (
                <div className="flex items-center space-x-3 p-2 bg-indigo-50 rounded">
                  <i className="fas fa-globe text-indigo-600"></i>
                  <div>
                    <div className="font-medium">Exportateur</div>
                    <div className="text-sm text-gray-600">{pesee.exportateur_nom}</div>
                    {pesee.exportateur_info?.email && (
                      <div className="text-xs text-gray-500">{pesee.exportateur_info.email}</div>
                    )}
                  </div>
                </div>
              )}

              {pesee.camion_immatriculation && (
                <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                  <i className="fas fa-truck text-gray-600"></i>
                  <div>
                    <div className="font-medium">Véhicule</div>
                    <div className="text-sm text-gray-600">Camion: {pesee.camion_immatriculation}</div>
                    {pesee.remorque_info?.immatriculation && (
                      <div className="text-sm text-gray-600">Remorque: {pesee.remorque_info.immatriculation}</div>
                    )}
                  </div>
                </div>
              )}

              {pesee.produit_nom && (
                <div className="flex items-center space-x-3 p-2 bg-purple-50 rounded">
                  <i className="fas fa-box text-purple-600"></i>
                  <div>
                    <div className="font-medium">Produit</div>
                    <div className="text-sm text-gray-600">{pesee.produit_nom}</div>
                    {pesee.produit_info?.code && (
                      <div className="text-xs text-gray-500">Code: {pesee.produit_info.code}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Message si aucun acteur */}
              {!pesee.client_nom && !pesee.fournisseur_nom && !pesee.chauffeur_nom && !pesee.exportateur_nom && !pesee.camion_immatriculation && !pesee.produit_nom && (
                <div className="text-center text-gray-500 py-4">
                  <i className="fas fa-info-circle text-2xl mb-2"></i>
                  <p>Aucune information d'acteur disponible pour cette pesée</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification */}
      {showModificationModal && (
        <PeseeModificationModal
          isOpen={showModificationModal}
          pesee={{
            ...pesee,
            site: 1 // Ajouter une valeur par défaut pour le site
          } as any}
          onClose={() => {
            setShowModificationModal(false);
            onClose();
          }}
          onSuccess={(message) => {
            console.log('Modification réussie:', message);
            setShowModificationModal(false);
            onClose();
            // Optionnel: déclencher un refresh de la liste
            onAction('modifier', pesee);
          }}
        />
      )}

      {/* Modal de refoulement */}
      {showRefoulementModal && (
        <RefoulementModal
          isOpen={showRefoulementModal}
          onClose={() => {
            setShowRefoulementModal(false);
            onClose(); // Fermer le menu quand on ferme le modal de refoulement
            // Déclencher un refresh de la liste après fermeture
            onAction('refouler', pesee);
          }}
          onSuccess={() => {
            // Ne pas fermer le modal ici, laisser le modal afficher le ticket
            // Le modal se fermera quand l'utilisateur fermera le ticket
            // Le refresh sera déclenché dans onClose
          }}
          pesee={pesee}
          peseeId={typeof pesee.id === 'string' ? parseInt(pesee.id) : pesee.id}
          peseeReference={pesee.reference || pesee.référence}
          autoLoadPesee={false}
          showTicketAfterCreation={true}
        />
      )}
    </>
  );
};

export default HistoryActionsMenu;
